/*
The SiteLandingPage.tsx file is a React component that serves as the main landing page for a site. It uses TypeScript for static type checking.
The file starts by defining two TypeScript interfaces: TypographyData and SiteLandingPageProps. TypographyData describes the properties of a typography object, while SiteLandingPageProps describes the properties that the SiteLandingPage component expects to receive.
The SiteLandingPage component uses several hooks from React, such as useState and useEffect, to manage state and side effects. It also uses styled-components for CSS-in-JS styling.
The file contains several styled components, such as Logo, App, AppContent, MainWrapper, Main, EditingContainer, StyledText, StyledSubMenuItem, CustomGridLines, StyledButton, and StyledDialog.
The SiteLandingPage component receives a large number of props, including functions for updating the site, saving the site, adding a child, updating widget position, updating config, setting active widget ID, updating the current page, toggling grid display, saving site pages, creating a page, removing a page, saving a page, initiating logout, and more.
The component also defines several helper functions, such as getMaxYLocation and containerHeight, which are used to calculate the maximum Y location and container height, respectively.
The SiteLandingPage component uses these props and helper functions to render a complex UI with a sidebar, menu, panels, widgets, and more. It also handles user interactions such as submitting the site, setting site data, and saving the site.
*/

import { FC, useEffect, useRef, useState } from 'react';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import styled, { keyframes } from 'styled-components';
import { PagesPanel } from '../PagesPanel/PagesPanel';
import { SiteStylesPanel } from '../SiteStylesPanel/SiteStylesPanel';
import { SettingsPanel } from '../SettingsPanel/SettingsPanel';
import { WidgetsPanel } from '../WidgetsPanel/WidgetsPanel';
import BuilderTopMenu from 'builder/components/BuilderTopMenu';
import { IDP } from 'shared/api/idx';
import { FOOTER_SECTION_ID, NO_IDP, widgetTypes } from 'builder/util/constants';
import '../WidgetStyles.css';
import GridLines from 'react-gridlines';
import WidgetControlWrapperContainerNew from '../WidgetControlWrapperNew';
import WidgetConnectorContainerNew from '../WidgetConnector';
import { comparePageJSONObjects } from 'shared/state/ducks/site';
import userManager from 'builder/util/userManager';
import { History } from 'history';
import {
  updateFormWidget as doUpdateFormWidget,
  createFormWidget as doCreateFormWidget,
} from 'shared/api/sites';
import { searchWidgetsByType } from 'shared/widgets';
import '../ThemeStyles.css';
import { IconLogout2, IconDoorExit } from '@tabler/icons-react';
import { Features } from 'shared/state/ducks/features';
import useDragSelection from '../../../hooks/useDragSelection';
import InlineNotification from '../../../../shared/components/InlineNotification';
import UserSyncChangesDialogContainer from 'shared/components/UserSycChangesDialog/UserSyncChangesDialogContainer';
import Loading from 'builder/scenes/Loading';
import { toast } from 'react-toastify';
import { ThemeType } from 'shared/util/ThemeType';
import { HarmonyTheme } from '@uhg-harmony/foundations';
import { defaultTheme } from 'shared/theme/theme';

const Logo = styled.img`
  height: 38px;
  width: 60px;
  max-width: 60px;
`;

const logo = (
  <Logo
    src={
      'https://cdn-stage.linkhealth.com/site-builder/horizon/HorizonLogo.png'
    }
    alt="optum-horizon-logo"
  />
);

const App = styled.div`
  box-sizing: border-box;
  @media only screen and (min-width: 980px) {
    display: flex;
    flex-direction: column;
  }
`;

const AppContent = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 980px) {
    flex-direction: row;
  }
`;

const MainWrapper = styled.div`
  flex-direction: row;
  width: 95%;
  overflow-x: hidden;
`;

const Main = styled.main<{
  scrollHeight: number;
}>`
  font-family: OptumSans, sans-serif;
  margin: 20px 0 20px 0;
  flex-direction: row;
  width: 100%;
  height: ${p => p.scrollHeight - 76}px;
`;
const EditingContainer = styled.div`
  height: 100%;
  border: 3px dotted #000;
  position: relative;
  width: 1280px;
  margin: auto;
`;

const spinAnimation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const PageLoadingSpinner = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.5);

  &::after {
    content: '';
    width: 48px;
    height: 48px;
    border: 4px solid #e0e0e0;
    border-top-color: #002677;
    border-radius: 50%;
    animation: ${spinAnimation} 0.8s linear infinite;
  }
`;

const StyledText = styled.p`
  color: ${defaultTheme.colors.aux.darkNavyBlue};
  font-weight: 700;
  font-size: 15px;
  font-family: OptumSans, sans-serif;
  margin-left: 9px;
`;

const StyledSubMenuItem = styled(MenuItem)``;

const CustomGridLines = styled(GridLines)`
  height: 100%;
`;

export interface SiteLandingPageProps<T> {
  widgetState: Widget<T>;
  site: Site;
  oldSite?: any;
  newSite?: any;
  pages: Array<Page>;
  page?: Page;
  defaultPage?: Page;
  idps?: Array<IDP>;
  provisioningStores: Array<ProvisioningStore>;
  updateSite: (site: Site) => void;
  saveSite: (showToastMessage?: boolean) => Promise<Site>;
  fetchIdps: () => void;
  fetchProvisioningStores: () => void;
  fetchSitesPublishedMessages?: (siteId: number) => void;
  fetchAllSiteVersions?: (siteId: number) => void;
  setResetToFalse: () => void;
  currentPage: PageVersion;
  addChild: (
    type: string,
    initialConfig: T,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
  updateWidgetPosition: (
    bannerWidgetId: string,
    height: number,
    oldHeight: number
  ) => void;
  onUpdateConfig: (config: any) => void;
  setActiveWidgetId: (widgetId: string) => void;
  activeWidgetId: string;
  updateCurrentPage: (pageVersion: PageVersion) => void;
  updatePageDefaultWidget: (
    widgetId: string,
    config: any,
    xDifference: number,
    yDifference: number
  ) => void;
  toggleShowGrid: () => void;
  shouldDisplayGridLines: boolean;
  onFlush?: () => void;
  saveSitePages?: (site: Site, pages: Array<Page>) => void;
  hasChanged?: boolean;
  swapResetData?: () => void;
  features?: Features;
  fetchFeatures?: (featureName: string) => void;
  resetUserSyncChange?: () => void;
  createPage: (page: Partial<PageVersion>) => void;
  removePage: (pageId: number) => void;
  savePage: (
    pageId: number,
    page: PageVersion,
    skipSiteRefresh?: boolean
  ) => Promise<void>;
  fetchCurrentSiteVersion?: () => Promise<void>;
  initiateLogout: () => void;
  history: History;
  isAccordionPanelOpen?: boolean;
  accordionHeightShift?: number;
  eventList?: Array<EventData>;
  fetchEventList?: (widgetId: string) => Promise<Array<EventData>>;
  saveEvent?: (
    event: EventPayload,
    siteId: number,
    pageId: number
  ) => Promise<EventData>;
  removeEvent?: (eventId: number) => Promise<void>;
  updateEvent?: (
    eventId: number,
    eventPayload: EventPayload
  ) => Promise<EventData>;
  isEditingTemplate: boolean;
  setSelectedPageTemplate: (templateId: number | null) => void;
  resetSiteDataForTemplateEditing: () => void;
  setIsEditingTemplate: (isEditing: boolean) => void;
  openSiteListDialog?: (
    dialogType: string,
    siteId: number,
    siteName: string
  ) => void;
  fetchCompareSiteandPage?: (siteId: number) => void;
  clearAllUndoRedo?: () => void;
  clearSelectedWidgetIds: () => void;
  groupWidgets: (currentPage: Page) => void;
  ungroupWidgets: (currentPage: Page) => void;
  setThemeType?: (themeType: ThemeType) => void;
  setTheme?: (themeType: HarmonyTheme | Theme | null) => void;
  fetchPage?: () => void;
  fetchAllUnloadedPages?: () => Promise<Array<Page>>;
  hasPendingDefaultWidgetFetches?: boolean;
}
export const getMaxYLocation = (currentPage: PageVersion): number => {
  let maxY = 0,
    yLocation;
  // Defensive: return 0 if currentPage or its content is missing to avoid runtime errors.
  if (!currentPage || !currentPage.content) {
    return maxY;
  }

  const footerSectionId = FOOTER_SECTION_ID;
  const footerEnabled = currentPage.footerEnabled;
  const getParentId = (widgetId: string): string | undefined => {
    const configuredParentId = currentPage.content[widgetId]?.config?.parentId;
    if (configuredParentId) {
      return configuredParentId;
    }

    const candidateParentIds = Object.keys(
      currentPage.content
    ).filter(parentId =>
      currentPage.content[parentId]?.children?.includes(widgetId)
    );

    return (
      candidateParentIds.find(parentId => parentId !== 'root') ??
      candidateParentIds[0]
    );
  };

  const isFooterDescendant = (widgetId: string): boolean => {
    let currentId = widgetId;
    while (currentId && currentId !== 'root') {
      if (currentId === footerSectionId) {
        return true;
      }
      currentId = getParentId(currentId);
    }
    return false;
  };

  function shouldSkipHeightCalculation(item) {
    return isFooterDescendant(item.id) && !footerEnabled;
  }

  // Compute effective footer yLocation locally — never mutate the shared Redux
  // footer object. The footer widget is shared by reference across all pages;
  // writing to config.yLocation here corrupts every other page's footer.
  let effectiveFooterYLocation =
    currentPage.content[FOOTER_SECTION_ID]?.config.yLocation;

  Object.keys(currentPage.content).forEach(item => {
    if (currentPage.content[item].type === widgetTypes.Application) {
      const appHeight = currentPage.content[item]?.config.height;
      if (
        effectiveFooterYLocation !== undefined &&
        effectiveFooterYLocation < appHeight
      ) {
        effectiveFooterYLocation =
          effectiveFooterYLocation +
          (appHeight - effectiveFooterYLocation) +
          20;
      }
    }
  });

  Object.keys(currentPage.content).forEach(item => {
    const widgetObj = currentPage.content[item];
    const parentId = widgetObj.config?.parentId;
    // Skip nested children — their extent is captured via the parent widget's effective bottom below.
    if (parentId && parentId !== 'root') {
      return;
    }
    if (
      widgetObj.config.yLocation === undefined ||
      widgetObj.config.height === undefined
    ) {
      yLocation = 900;
    } else {
      const resolvedYLocation =
        item === FOOTER_SECTION_ID && effectiveFooterYLocation !== undefined
          ? effectiveFooterYLocation
          : widgetObj.config.yLocation;

      // For non-root parent widgets, use the deepest visible child's bottom as
      // the effective height so content extending beyond the widget's stored
      // height is included in canvas sizing.
      let effectiveHeight = widgetObj.config.height;
      if (item !== 'root') {
        (widgetObj.children || []).forEach(childId => {
          const child = currentPage.content[childId];
          if (!child) return;
          if (child.config?.visible === false) return;
          if (shouldSkipHeightCalculation(child)) return;
          if (
            child.config?.yLocation !== undefined &&
            child.config?.height !== undefined
          ) {
            const childBottom =
              child.config.yLocation + child.config.height - resolvedYLocation;
            if (childBottom > effectiveHeight) {
              effectiveHeight = childBottom;
            }
          }
        });
      }

      yLocation =
        resolvedYLocation +
        (shouldSkipHeightCalculation(widgetObj) ? 0 : effectiveHeight) +
        90;
    }

    if (yLocation > maxY && !shouldSkipHeightCalculation(widgetObj)) {
      maxY = yLocation;
    }
  });

  return maxY;
};

export const containerHeight = (
  currentPage: PageVersion,
  isAccordionPanelOpen: boolean,
  accordionHeightShift: number
) => {
  if (currentPage) {
    if (!currentPage?.content || Object.keys(currentPage.content).length <= 1) {
      return 900;
    }
  }

  const maxHeight = getMaxYLocation(currentPage);
  const stickyBottomBannerHeight = Object.values(
    currentPage.content || {}
  ).find(
    (widget: any) =>
      widget?.type === widgetTypes.Banner &&
      widget?.config?.placement === 'Sticky Bottom (Fixed to Screen)' &&
      widget?.config?.visible !== false
  )?.config?.height;
  const footerHeight = currentPage.content?.[FOOTER_SECTION_ID]?.config?.height;
  // Reserve only the larger of sticky banner/footer heights to avoid
  // over-padding after footer while still keeping footer clear of the strip.
  const stickyBottomOffset =
    typeof stickyBottomBannerHeight === 'number'
      ? Math.max(
          stickyBottomBannerHeight,
          typeof footerHeight === 'number' ? footerHeight : 0
        )
      : 0;

  if (isAccordionPanelOpen) {
    return maxHeight + stickyBottomOffset + accordionHeightShift;
  }
  return maxHeight + stickyBottomOffset;
};

/**
 * Filters pages to only include those with real server content.
 * Excludes pages whose content is still the placeholder template (content hasn't
 * arrived from the server yet) to prevent overwriting real page data on save.
 */
export { filterValidPages } from 'shared/util/pageUtils';
import { filterValidPages } from 'shared/util/pageUtils';

export const SiteLandingPage: FC<SiteLandingPageProps<any>> = (
  props: SiteLandingPageProps<any>
) => {
  const {
    site,
    pages,
    page,
    idps,
    provisioningStores,
    currentPage,
    updateSite,
    saveSite,
    addChild,
    updateWidgetPosition,
    widgetState,
    onUpdateConfig,
    updateCurrentPage,
    toggleShowGrid,
    shouldDisplayGridLines,
    setResetToFalse,
    saveSitePages,
    hasChanged,
    resetUserSyncChange,
    savePage,
    createPage,
    removePage,
    defaultPage,
    updatePageDefaultWidget,
    initiateLogout,
    history,
    isAccordionPanelOpen,
    accordionHeightShift,
    eventList,
    fetchEventList,
    saveEvent,
    removeEvent,
    updateEvent,
    openSiteListDialog,
    isEditingTemplate,
    setIsEditingTemplate,
    setSelectedPageTemplate,
    resetSiteDataForTemplateEditing,
    oldSite,
    newSite,
    features,
    fetchFeatures,
    clearAllUndoRedo,
    clearSelectedWidgetIds,
    groupWidgets,
    ungroupWidgets,
    fetchSitesPublishedMessages,
    fetchAllSiteVersions,
    setThemeType,
    setTheme,
    fetchPage,
    fetchAllUnloadedPages,
    hasPendingDefaultWidgetFetches,
    fetchCurrentSiteVersion,
  } = props;

  // Exclude pages whose content is still the placeholder template — their
  // real content hasn't arrived from the server yet.  Saving placeholder
  // content would overwrite the real page data on the server.
  const filteredPages = filterValidPages(pages);

  // True when the target page exists in the URL but its content hasn't
  // arrived from the server yet (it's still a placeholder in state).
  // Computed early so it can be used as a useEffect dependency below.
  const targetPageIsLoading = !!(
    page?.id && !filteredPages.some(p => p.id === page.id)
  );

  // All pages with valid metadata (id, title, slug) regardless of whether
  // content is loaded.  Used for link/button/nav page-selector dropdowns
  // so all site pages appear as options, not just the visited ones.
  const selectablePages = pages.filter(
    page => page.current && page.current.id !== null
  );

  const [openConfigPanel, setOpenConfigPanel] = useState(false);
  const [activeWidgetId, setActiveWidgetId] = useState('root');
  const [location, setLocation] = useState(undefined);
  const [_currentPageId, setCurrentPageId] = useState('');
  const [isNewPage, setIsNewPage] = useState(false);
  const [showToastMessage, setShowToastMessage] = useState(true);

  // Cache the last successfully resolved page/currentPage so we can keep
  // showing it while a newly-selected page is still being fetched.
  const lastValidPageRef = useRef<Page | undefined>(undefined);
  const lastValidCurrentPageRef = useRef<PageVersion | undefined>(undefined);

  const { containerRef, isSelecting, SelectionBox } = useDragSelection(
    currentPage
  );

  // Always-current ref so async save callbacks read the latest filteredPages
  // rather than the stale closure captured at the time hasChanged changed.
  const filteredPagesRef = useRef(filteredPages);
  filteredPagesRef.current = filteredPages;

  // Track whether a header/footer (defaultWidget) edit is pending so we
  // don't call fetchAllUnloadedPages on every save (e.g. the initial
  // site-open save), which would cause all pages to be fetched upfront.
  const hasPendingDefaultWidgetFetchesRef = useRef(
    hasPendingDefaultWidgetFetches
  );
  hasPendingDefaultWidgetFetchesRef.current = hasPendingDefaultWidgetFetches;

  useEffect(() => {
    saveSitePages(site, filteredPages);
  }, []);

  // Stale-while-revalidate: always fetch fresh page content from the server
  // when the target page changes.  The overlay only shows when content is
  // genuinely missing (placeholder), so on re-entry into the editor the user
  // sees cached content immediately while the background fetch updates it
  // silently.  Without the unconditional fetch here, re-entering the editor
  // would never refresh content since FETCH_PAGES_FULFILLED now preserves
  // existing content instead of resetting it to the placeholder.
  useEffect(() => {
    if (page?.id) {
      fetchPage?.();
    }
  }, [page?.id]);

  useEffect(() => {
    const { config } = props.widgetState ?? {};
    if (config) {
      onUpdateConfig({ ...config, ...location });
    }
  }, [location]);

  useEffect(() => {
    if (!hasChanged && hasChanged !== undefined) {
      // showToastMessage is false when triggered by the autosave interval
      // (which already calls doFetchSite internally via fetchCompareSiteandPage)
      // and true when triggered by an explicit user save action.
      // Capture the value now so the async .then() below uses the right flag.
      const isExplicitSave = showToastMessage;
      saveSite(showToastMessage).then(async response => {
        // Only wait for background page fetches if a header/footer
        // (defaultWidget) edit was made with unloaded pages present.
        // Skipping this on normal saves avoids fetching all pages upfront.
        // Start from the ref (already-loaded pages). filteredPages in the
        // closure is stale, but the ref is updated on every render.
        let latestFilteredPages = filteredPagesRef.current;
        if (hasPendingDefaultWidgetFetchesRef.current) {
          // fetchAllUnloadedPages fetches placeholder pages and returns the
          // full filtered page list from fresh Redux state — avoiding the stale
          // ref problem where React hasn't re-rendered yet after the dispatches.
          try {
            const freshlyFetchedPages = (await fetchAllUnloadedPages?.()) ?? [];
            if (freshlyFetchedPages.length > 0) {
              const existingIds = new Set(latestFilteredPages.map(p => p.id));
              latestFilteredPages = [
                ...latestFilteredPages,
                ...freshlyFetchedPages.filter(p => !existingIds.has(p.id)),
              ];
            }
          } catch (error) {
            console.error('Failed to fetch unloaded pages:', error);
            toast.error(
              'Some pages could not be loaded. Your save was not completed. Please try again.',
              { toastId: 'fetch-unloaded-pages-error' }
            );
            return;
          }
        }
        saveSitePages(response, latestFilteredPages);
        resetUserSyncChange();
        // Save all pages in parallel with skipSiteRefresh=true, then call
        // fetchCurrentSiteVersion ONCE after all saves complete — but only
        // for explicit user saves. Autosave already refreshes via
        // fetchCompareSiteandPage, so a second /current call is unnecessary.
        Promise.all(
          latestFilteredPages.map(page =>
            handleSavePage(page.id, page.current, true)
          )
        ).then(() => {
          if (isExplicitSave) {
            fetchCurrentSiteVersion?.();
          }
        });
      });
    }
    setShowToastMessage(true);
  }, [hasChanged]);

  useEffect(() => {
    // Adding this listener because history.push is only working in this component if a force reload via history.go(0)
    const unListen = history.listen(() => {});
    return () => {
      unListen();
    };
  }, [history]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        site.autoSave &&
        checkPagesData(Object.values(newSite.pages), oldSite?.pages)
      ) {
        fetchFeatures('duplo.json');
        setShowToastMessage(false);
        props.fetchCompareSiteandPage(site.siteId);
      }
    }, features.autoSaveTimer.time || 5000); // check time value from ui-config or set default to 5 seconds
    return () => clearInterval(interval);
  }, [newSite.pages, oldSite?.pages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const payload = {
        ...page,
        current: currentPage,
      };
      if ((event.ctrlKey || event.metaKey) && event.key === 'g') {
        groupWidgets(payload);
        event.preventDefault();
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 'u') {
        ungroupWidgets(payload);
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [page, currentPage]);

  const removeChildrenShim = (site: Site) => {
    site.header.nav.items.forEach(item => {
      // Todo: This will never be accurate because item.type cannot be "section" because it is "Section".
      //  So what is this doing and is it a defect? This could be avoided if used the constants from the utils folder.
      if (item.type !== 'section' && item.children !== undefined) {
        item.children = undefined;
      }
      if (item.children !== undefined) {
        item.children.forEach(childItem => {
          if (
            // Todo: This will never be accurate because item.type cannot be "section" because it is "Section".
            //  So what is this doing and is it a defect? This could be avoided if used the constants from the utils folder.
            childItem.type !== 'section' &&
            childItem.children !== undefined
          ) {
            childItem.children = undefined;
          }
        });
      }
    });
  };

  const handleSubmit = (site: Site) => {
    removeChildrenShim(site);
    const { updateSite, saveSite } = props;
    updateSite(site);
    saveSite(showToastMessage);
  };

  const checkPagesData = (a: Array<Page>, b: Array<Page>) => {
    return !comparePageJSONObjects(a, b);
  };

  const getLocationData = loc => {
    setLocation(loc);
  };

  const getOpenConfigPanel = (open: boolean) => {
    setOpenConfigPanel(open);
  };

  // prevents ghost image from being sent back to widgets panel after dropping
  const handleDragOver = e => {
    e.preventDefault();
  };

  const handleActiveWidgetId = (widgetId: string) => {
    setActiveWidgetId(widgetId);
  };

  const handleFormWidget = (action: string, widgetId: String): Promise<any> => {
    const { site, page } = props;
    const widget = searchWidgetsByType(
      page.current.content,
      'root',
      new Array<Widget<any>>(),
      widgetTypes.Form
    ).find(widget => widget.id === widgetId);
    if (widget.config.formActionType === 'email') {
      const conditionalEmailList = [
        ...(widget.config.conditionalEmailConfig || []),
      ].map(conditionalEmail => ({
        ...conditionalEmail,
        email: [conditionalEmail.email],
      }));
      switch (action) {
        case 'create':
          return doCreateFormWidget(site.siteId, page.id, {
            widgetId: widget.id,
            recipientEmails: [widget.config.emailTo],
            enableConditionalEmailTo: widget.config.conditionalEmailEnabled,
            conditionalEmailList: conditionalEmailList,
          });
        case 'update':
          return doUpdateFormWidget(site.siteId, page.id, {
            widgetId: widget.id,
            recipientEmails: [widget.config.emailTo],
            enableConditionalEmailTo: widget.config.conditionalEmailEnabled,
            conditionalEmailList: conditionalEmailList,
          });
        case 'delete':
          break;
        default:
          break;
      }
    }
  };

  const handleSavePage = (
    pageId: number,
    pageVersion: PageVersion,
    skipSiteRefresh?: boolean
  ): Promise<void> => {
    const { forms } = pageVersion;
    forms?.create?.forEach(item => {
      handleFormWidget('create', item);
    });
    forms?.update?.forEach(item => {
      handleFormWidget('update', item);
    });

    return savePage(pageId, pageVersion, skipSiteRefresh);
  };

  const handleBack = () => {
    let path = '/sites';
    if (isEditingTemplate) {
      resetSiteDataForTemplateEditing();
      setSelectedPageTemplate(null);
      setIsEditingTemplate(false);
      path = '/page-templates';
    }
    clearAllUndoRedo();
    history.push(path);
  };

  const handleLogout = () => {
    initiateLogout();
    userManager.signoutRedirect({
      post_logout_redirect_uri: window.location,
    });
  };

  const handleCurrentPageData = pageData => {
    setCurrentPageId(pageData);
  };

  const handleNewPage = newPage => {
    setIsNewPage(newPage);
  };

  const lockedLayoutNotificationText =
    'The page you are editing has a locked layout. Some features such as adding and removing widgets, moving and resizing widgets, as well as some widget configurations are disabled.';

  const currentPageInFilteredPages =
    currentPage &&
    filteredPages.find(page => {
      return page.current.id === currentPage.id;
    });

  let validCurrentPage: PageVersion | undefined;
  let validPage: Page | undefined;

  if (targetPageIsLoading) {
    // While the page is fetching, hold the previous page in the canvas so
    // the user doesn't get bounced to the homepage URL.
    validPage = lastValidPageRef.current;
    validCurrentPage = lastValidCurrentPageRef.current;
  } else {
    validCurrentPage = currentPageInFilteredPages
      ? currentPage
      : filteredPages.find(
          page => page.current && page.current.pageId === site.horizonhomepageId
        )?.current;
    validPage = currentPageInFilteredPages
      ? page
      : filteredPages.find(
          page => page.current && page.current.pageId === site.horizonhomepageId
        );

    if (validPage && validCurrentPage) {
      lastValidPageRef.current = validPage;
      lastValidCurrentPageRef.current = validCurrentPage;
    }
  }

  // Pages not loaded yet — show loading state until home page content arrives.
  if (!validPage || !validCurrentPage) {
    return <Loading message="Loading site" />;
  }

  return (
    <>
      <App onDragOver={handleDragOver}>
        <AppContent data-test-id="app-content">
          <Sidebar
            style={{
              height: '919px',
              width: '79px',
              position: 'sticky',
              zIndex: 999,
              top: 0,
              backgroundColor: '#FFFFFF',
            }}
            defaultCollapsed
            data-test-id="sticky-sidebar"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '95vh',
                maxHeight: '55rem',
              }}
            >
              <Menu>
                <MenuItem icon={logo} data-test-id="logo-menu-item"></MenuItem>
                <StyledText data-test-id="horizon-label">Horizon</StyledText>
                <WidgetsPanel
                  getLocationData={getLocationData}
                  pages={filteredPages}
                  currentPage={validCurrentPage}
                  updateSite={updateSite}
                  addChild={addChild}
                  widgetState={widgetState}
                  onUpdateConfig={onUpdateConfig}
                  setResetToFalse={setResetToFalse}
                  updateWidgetPosition={updateWidgetPosition}
                  site={site}
                  isEditingTemplate={isEditingTemplate}
                />
                {!isEditingTemplate && (
                  <div data-test-id="editor-page-site-controls">
                    <SiteStylesPanel
                      site={site}
                      updateSite={updateSite}
                      saveSite={saveSite}
                      pages={filteredPages}
                      updateCurrentPage={updateCurrentPage}
                      setThemeType={setThemeType}
                      setTheme={setTheme}
                    />
                    <PagesPanel
                      pages={pages}
                      siteDetails={site}
                      currentPage={validCurrentPage}
                      createPage={createPage}
                      removePage={removePage}
                      savePage={savePage}
                      handleActiveWidgetId={handleActiveWidgetId}
                      defaultPage={defaultPage}
                      handleCurrentPageData={handleCurrentPageData}
                      handleNewPage={handleNewPage}
                      openPopup={getOpenConfigPanel}
                      updateSite={updateSite}
                      saveSite={saveSite}
                      updateCurrentPage={updateCurrentPage}
                    />
                    <SettingsPanel
                      siteDetails={{
                        ...site,
                        idpFlag: site.idpHint !== NO_IDP ? 'enable' : 'disable',
                        header: {
                          ...site.header,
                          feedback: {
                            ...site.header.feedback,
                            formFields: site.header.feedback?.formFields || [],
                          },
                        },
                      }}
                      pages={filteredPages}
                      idps={idps}
                      siteIdp={site.idpHint}
                      provisioningStores={provisioningStores}
                      onSubmit={handleSubmit}
                      updateSite={updateSite}
                    />
                  </div>
                )}
              </Menu>
              <Menu>
                <StyledSubMenuItem
                  data-test-id="back-to-sites-button"
                  onClick={handleBack}
                  style={{ marginTop: 'auto' }}
                  title={`Back to all ${
                    isEditingTemplate ? 'templates' : 'sites'
                  }`}
                >
                  <IconDoorExit size={36} stroke={2} color="#2E3034" />
                </StyledSubMenuItem>
                <StyledSubMenuItem
                  data-test-id="logout-button"
                  onClick={handleLogout}
                  style={{ marginTop: '1rem' }}
                  title="Sign out of Horizon"
                >
                  <IconLogout2 size={36} stroke={2} color="#2E3034" />
                </StyledSubMenuItem>
              </Menu>
            </div>
          </Sidebar>
          <MainWrapper>
            {currentPage?.layoutLocked && (
              <InlineNotification
                dontShowAgain
                id={validCurrentPage?.pageId}
                message={lockedLayoutNotificationText}
                type="note"
              />
            )}
            <BuilderTopMenu
              siteId={site.siteId}
              pageId={validCurrentPage?.pageId}
              toggleShowGrid={toggleShowGrid}
              closeConfigPanel={getOpenConfigPanel}
              isPreviewMode={false}
              currentPageId={validCurrentPage.id}
              isNewPage={isNewPage}
              openSiteListDialog={openSiteListDialog}
              fetchSitesPublishedMessages={fetchSitesPublishedMessages}
              fetchAllSiteVersions={fetchAllSiteVersions}
            />
            <Main
              tabIndex={-1}
              id="main"
              scrollHeight={containerHeight(
                validCurrentPage,
                isAccordionPanelOpen,
                accordionHeightShift
              )}
              style={{
                backgroundColor: validCurrentPage.pageBackground?.color
                  ? validCurrentPage.pageBackground?.color
                  : '#FFFFFF',
              }}
            >
              <EditingContainer
                className="editingContainer"
                id="editingContainerId"
                ref={containerRef}
                style={
                  targetPageIsLoading ? { pointerEvents: 'none' } : undefined
                }
                onClick={() => {
                  if (!isSelecting) {
                    clearSelectedWidgetIds();
                  }
                }}
              >
                {targetPageIsLoading && (
                  <PageLoadingSpinner
                    aria-busy={true}
                    role="status"
                    aria-label="Loading page"
                  />
                )}
                <SelectionBox />
                <CustomGridLines
                  className="grid-area"
                  cellWidth={shouldDisplayGridLines ? 120 : 0}
                  strokeWidth={2}
                  cellWidth2={10}
                >
                  <WidgetConnectorContainerNew
                    addChild={addChild}
                    siteId={site.siteId}
                    pageId={validCurrentPage.pageId}
                    widgetId={activeWidgetId}
                    openPopup={getOpenConfigPanel}
                    currentPage={validCurrentPage}
                    getActiveWidgetId={handleActiveWidgetId}
                    updateCurrentPage={updateCurrentPage}
                    updatePageDefaultWidget={updatePageDefaultWidget}
                    closeConfigPanel={getOpenConfigPanel}
                    editing={true}
                    page={validPage}
                    updateWidgetPosition={updateWidgetPosition}
                    pages={filteredPages}
                    initiateLogout={initiateLogout}
                    site={site}
                  />
                </CustomGridLines>
              </EditingContainer>
              {openConfigPanel && (
                <WidgetControlWrapperContainerNew
                  siteId={site.siteId}
                  pageId={validCurrentPage.pageId}
                  pages={selectablePages}
                  widgetId={activeWidgetId}
                  site={site}
                  updateSite={updateSite}
                  saveSite={saveSite}
                  openPopup={getOpenConfigPanel}
                  addChild={addChild}
                  currentPage={validCurrentPage}
                  updateCurrentPage={updateCurrentPage}
                  updateWidgetPosition={updateWidgetPosition}
                  eventList={eventList}
                  fetchEventList={fetchEventList}
                  saveEvent={saveEvent}
                  removeEvent={removeEvent}
                  updateEvent={updateEvent}
                />
              )}
            </Main>
          </MainWrapper>
        </AppContent>
      </App>
      <UserSyncChangesDialogContainer
        siteId={site.siteId}
        site={site}
        pageId={validCurrentPage?.pageId}
        showToastMessage={showToastMessage}
      />
    </>
  );
};
